import { getCookie, setCookie, deleteCookie } from "cookies-next/client";

export interface SessionData {
  sessionId: string;
  startedAt: string;
  lastActivity: string;
  funnel: {
    type: "story" | "search" | "general";
    context?: Record<string, any>;
  };
}

export class SessionManager {
  private static SESSION_COOKIE = "sessionId";
  private static SESSION_DATA_COOKIE = "sessionData";
  private static SESSION_DURATION = 60 * 30; // 30 minutes

  /**
   * Generate a new unique session ID
   */
  private static generateSessionId(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    // Fallback UUID v4 generator
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Get current session ID
   */
  static getSessionId(): string | null {
    return getCookie(this.SESSION_COOKIE) || null;
  }

  /**
   * Get full session data
   */
  static getSessionData(): SessionData | null {
    const data = getCookie(this.SESSION_DATA_COOKIE);
    if (!data) return null;

    try {
      return JSON.parse(data as string);
    } catch {
      return null;
    }
  }

  /**
   * Create a new session
   */
  static createSession(
    funnelType: "story" | "search" | "general",
    context?: Record<string, any>
  ): string {
    const sessionId = this.generateSessionId();
    const now = new Date().toISOString();

    const sessionData: SessionData = {
      sessionId,
      startedAt: now,
      lastActivity: now,
      funnel: {
        type: funnelType,
        context,
      },
    };

    // Set session ID cookie
    setCookie(this.SESSION_COOKIE, sessionId, {
      maxAge: this.SESSION_DURATION,
      path: "/",
      sameSite: "strict",
      secure: process.env.NODE_ENV === "production",
    });

    // Set session data cookie
    setCookie(this.SESSION_DATA_COOKIE, JSON.stringify(sessionData), {
      maxAge: this.SESSION_DURATION,
      path: "/",
      sameSite: "strict",
      secure: process.env.NODE_ENV === "production",
    });

    console.log(`🎯 New session created: ${sessionId} (${funnelType})`);
    return sessionId;
  }

  /**
   * Update existing session activity
   */
  static updateSessionActivity(): void {
    const sessionData = this.getSessionData();
    if (!sessionData) return;

    sessionData.lastActivity = new Date().toISOString();

    setCookie(this.SESSION_DATA_COOKIE, JSON.stringify(sessionData), {
      maxAge: this.SESSION_DURATION,
      path: "/",
      sameSite: "strict",
      secure: process.env.NODE_ENV === "production",
    });
  }

  /**
   * Check if current session matches the funnel type and context
   */
  static shouldCreateNewSession(
    funnelType: "story" | "search" | "general",
    context?: Record<string, any>
  ): boolean {
    const sessionData = this.getSessionData();

    // No session exists
    if (!sessionData) return true;

    // Different funnel type
    if (sessionData.funnel.type !== funnelType) return true;

    // For search funnel, check if city/state changed
    if (funnelType === "search" && context) {
      const currentContext = sessionData.funnel.context;
      if (!currentContext) return true;

      // New search with different city or state = new funnel
      if (
        context.city !== currentContext.city ||
        context.state !== currentContext.state
      ) {
        console.log("🔄 Search context changed, creating new session");
        return true;
      }
    }

    return false;
  }

  /**
   * Start or continue a story session
   */
  static startStorySession(storyGroupId: string): string {
    const context = { storyGroupId };

    if (this.shouldCreateNewSession("story", context)) {
      return this.createSession("story", context);
    }

    // Update existing session
    this.updateSessionActivity();
    return this.getSessionId()!;
  }

  /**
   * Start or continue a search session
   */
  static startSearchSession(city: string, state: string): string {
    const context = { city, state };

    if (this.shouldCreateNewSession("search", context)) {
      return this.createSession("search", context);
    }

    // Update existing session
    this.updateSessionActivity();
    return this.getSessionId()!;
  }

  /**
   * End current session
   */
  static endSession(): void {
    deleteCookie(this.SESSION_COOKIE);
    deleteCookie(this.SESSION_DATA_COOKIE);
    console.log("🛑 Session ended");
  }

  /**
   * Get or create a general session (for pages without specific funnel)
   */
  static ensureSession(): string {
    const sessionId = this.getSessionId();

    if (!sessionId) {
      return this.createSession("general");
    }

    this.updateSessionActivity();
    return sessionId;
  }
}
